import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Layers,
  CheckCircle2,
  Circle,
  Play,
  Pause,
  Timer,
  Clock,
  Image as ImageIcon,
  ZoomIn,
  ChevronRight,
  AlertTriangle,
  FileText,
  Shield,
  Paperclip,
  Download,
  ExternalLink,
  ChevronLeft,
  ChevronRightIcon,
  File,
  FileSpreadsheet,
} from 'lucide-react';
import { QualityInspectionPanel } from '@/components/Quality';
import { useAuth } from '@/contexts/AuthContext';
import {
  usePlybookExecution,
  useStartPlybookExecution,
  useConfirmPly,
  usePlybook,
  usePlybookPlies,
} from '@/hooks/usePlybooks';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { PLY_COLORS, MATERIAL_TYPE_LABELS, ATTACHMENT_TYPE_LABELS, formatFiberAngle } from '@/types/plybook';
import type { PlybookPly, PlybookZone, PlybookAttachment } from '@/types/plybook';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { PlybookLayerEditor } from './PlybookLayerEditor';

interface PlybookLaminationViewProps {
  workOrderId: string;
  plybookId: string;
  onComplete?: () => void;
  onBack?: () => void;
}

export function PlybookLaminationView({
  workOrderId,
  plybookId,
  onComplete,
  onBack,
}: PlybookLaminationViewProps) {
  const { user } = useAuth();
  const [activeZone, setActiveZone] = useState<string | null>(null);
  const [currentPlyStartTime, setCurrentPlyStartTime] = useState<Date | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; ply?: PlybookPly }>({ open: false });
  const [confirmNotes, setConfirmNotes] = useState('');
  const [imageZoom, setImageZoom] = useState(false);
  const [currentAttachmentIndex, setCurrentAttachmentIndex] = useState(0);

  // Fetch data
  const { data: execution, isLoading: executionLoading } = usePlybookExecution(workOrderId);
  const { data: plybook, isLoading: plybookLoading } = usePlybook(plybookId);
  // Cargar capas por separado para evitar problemas de cache
  const { data: plybookPlies, isLoading: pliesLoading } = usePlybookPlies(plybookId);

  // Fetch zones
  const { data: zones } = useQuery({
    queryKey: ['plybook-zones'],
    queryFn: async () => {
      const { data, error } = await api
        .from('plybook_zones')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data as PlybookZone[];
    },
  });

  // Fetch attachments for the plybook
  const { data: attachments } = useQuery({
    queryKey: ['plybook-attachments', plybookId],
    queryFn: async () => {
      const { data, error } = await api
        .from('plybook_attachments')
        .select('*')
        .eq('plybook_id', plybookId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as PlybookAttachment[];
    },
    enabled: !!plybookId,
  });

  // Mutations
  const startMutation = useStartPlybookExecution();
  const confirmMutation = useConfirmPly();

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (currentPlyStartTime) {
      interval = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - currentPlyStartTime.getTime()) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [currentPlyStartTime]);

  const isLoading = executionLoading || plybookLoading || pliesLoading;
  const activePlybook = execution?.plybook || plybook;
  // Usar plybookPlies (cargadas por separado) si no hay execution
  const plies = execution?.plybook?.plies || plybookPlies || [];
  const confirmedPlyNumbers = new Set(execution?.confirmations?.map((c) => c.ply_number) || []);

  // Group plies by zone
  const pliesByZone = useMemo(() => {
    const grouped: Record<string, PlybookPly[]> = {};
    plies.forEach((ply) => {
      const zone = ply.zone || 'Sin Zona';
      if (!grouped[zone]) grouped[zone] = [];
      grouped[zone].push(ply);
    });
    return grouped;
  }, [plies]);

  // Get zone color
  const getZoneColor = (zoneName: string) => {
    const zone = zones?.find(z => z.name === zoneName);
    return zone?.color || '#6b7280';
  };

  // Calculate zone progress
  const getZoneProgress = (zonePlies: PlybookPly[]) => {
    const confirmed = zonePlies.filter(p => confirmedPlyNumbers.has(p.ply_number)).length;
    return { confirmed, total: zonePlies.length, percent: (confirmed / zonePlies.length) * 100 };
  };

  // Get next ply
  const getNextPly = (): PlybookPly | undefined => {
    return plies.find((ply) => !confirmedPlyNumbers.has(ply.ply_number));
  };

  // Format time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get total time from confirmations
  const getTotalTime = () => {
    const totalSeconds = execution?.confirmations?.reduce((sum, c) => sum + (c.duration_seconds || 0), 0) || 0;
    const mins = Math.floor(totalSeconds / 60);
    return mins > 0 ? `${mins} min` : `${totalSeconds} seg`;
  };

  const nextPly = getNextPly();
  const progress = execution ? (execution.confirmed_plies / execution.total_plies) * 100 : 0;

  const handleStart = async () => {
    if (!user) return;
    await startMutation.mutateAsync({
      workOrderId,
      plybookId,
      operatorId: user.id,
    });
    setCurrentPlyStartTime(new Date());
  };

  const handleStartPly = (ply: PlybookPly) => {
    setCurrentPlyStartTime(new Date());
    setActiveZone(ply.zone || null);
  };

  const handleConfirmPly = async () => {
    if (!user || !confirmDialog.ply || !execution) return;

    const durationSeconds = currentPlyStartTime
      ? Math.floor((Date.now() - currentPlyStartTime.getTime()) / 1000)
      : undefined;

    await confirmMutation.mutateAsync({
      executionId: execution.id,
      plyId: confirmDialog.ply.id,
      plyNumber: confirmDialog.ply.ply_number,
      operatorId: user.id,
      notes: confirmNotes || undefined,
      durationSeconds,
      startedAt: currentPlyStartTime?.toISOString(),
    });

    setConfirmDialog({ open: false });
    setConfirmNotes('');
    setCurrentPlyStartTime(null);
    setElapsedSeconds(0);

    // Start timer for next ply automatically
    const nextAfterThis = plies.find(
      (p) => p.ply_number > confirmDialog.ply!.ply_number && !confirmedPlyNumbers.has(p.ply_number)
    );
    if (nextAfterThis) {
      setCurrentPlyStartTime(new Date());
    }

    if (execution.confirmed_plies + 1 >= execution.total_plies) {
      onComplete?.();
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Not started view
  if (!execution) {
    const hasPlies = plies.length > 0;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            {onBack && (
              <Button variant="ghost" onClick={onBack} className="text-white hover:bg-white/10">
                ← Volver
              </Button>
            )}
            <h1 className="text-3xl font-bold">{activePlybook?.name}</h1>
            <div />
          </div>

          {/* Visual Layer Editor */}
          {activePlybook?.main_image_url && hasPlies && (
            <div className="mb-8">
              <PlybookLayerEditor
                imageUrl={activePlybook.main_image_url}
                plies={plies}
                editable={true}
              />
            </div>
          )}
          
          {/* Image without layers */}
          {activePlybook?.main_image_url && !hasPlies && (
            <div className="mb-8 rounded-2xl overflow-hidden bg-black/30 p-4">
              <img
                src={activePlybook.main_image_url}
                alt={activePlybook.name}
                className="w-full h-auto max-h-[50vh] object-contain rounded-xl"
              />
            </div>
          )}

          {/* No plies warning */}
          {!hasPlies && (
            <div className="mb-8 p-6 rounded-2xl bg-red-900/30 border border-red-500/50 text-center">
              <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-red-400" />
              <h2 className="text-2xl font-bold text-red-400 mb-2">
                No hay capas definidas
              </h2>
              <p className="text-white/70 mb-4">
                Este plybook no tiene capas configuradas. Debes agregar las capas antes de iniciar el laminado.
              </p>
              <Button
                variant="outline"
                onClick={onBack}
                className="border-white/30 text-white hover:bg-white/10"
              >
                Volver y agregar capas
              </Button>
            </div>
          )}

          {/* Summary by zones - only show if has plies */}
          {hasPlies && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {Object.entries(pliesByZone).map(([zone, zonePlies]) => (
                  <Card key={zone} className="bg-white/10 border-0">
                    <CardContent className="p-4 text-center">
                      <div
                        className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: getZoneColor(zone) }}
                      >
                        {zonePlies.length}
                      </div>
                      <p className="text-white font-medium">{zone}</p>
                      <p className="text-white/60 text-sm">{zonePlies.length} capas</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Plies preview list */}
              <div className="mb-8 p-4 rounded-2xl bg-white/5">
                <h3 className="text-lg font-semibold mb-4">Secuencia de capas:</h3>
                <div className="flex flex-wrap gap-2">
                  {plies.map((ply) => (
                    <div
                      key={ply.id}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10"
                    >
                      <div
                        className="w-8 h-8 rounded flex items-center justify-center text-white font-bold text-sm"
                        style={{ backgroundColor: ply.color || PLY_COLORS[ply.material_type || 'other'] }}
                      >
                        {ply.ply_number}
                      </div>
                      <div className="text-sm">
                        <p className="font-medium">{ply.material_name || MATERIAL_TYPE_LABELS[ply.material_type || 'other']}</p>
                        <p className="text-white/50 text-xs">{formatFiberAngle(ply.fiber_angle)} - {ply.zone || 'Sin zona'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Info message */}
              <div className="text-center py-4">
                <p className="text-white/70 text-lg">{plies.length} capas totales</p>
                <p className="text-white/50 mt-2 text-sm">El laminado se inicia desde una Orden de Trabajo asignada a un laminador</p>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // In progress view
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 text-white">
      {/* Top bar */}
      <div className="bg-black/30 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold">{activePlybook?.name}</h1>
          <Badge className="bg-blue-600">{execution.confirmed_plies}/{execution.total_plies}</Badge>
        </div>

        <div className="flex items-center gap-6">
          {/* Quality Inspection Button */}
          <QualityInspectionPanel
            workOrderId={workOrderId}
            plybookExecutionId={execution.id}
            currentPlyNumber={nextPly?.ply_number}
            inspectionPoint="lamination"
            compact
          />

          {/* Timer */}
          <div className="flex items-center gap-2 text-2xl font-mono">
            <Timer className="w-6 h-6 text-yellow-400" />
            <span className={cn(currentPlyStartTime && "text-yellow-400")}>
              {formatTime(elapsedSeconds)}
            </span>
          </div>

          {/* Total time */}
          <div className="flex items-center gap-2 text-sm text-white/60">
            <Clock className="w-4 h-4" />
            Total: {getTotalTime()}
          </div>

          {/* Back button */}
          {onBack && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              ← Salir
            </Button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <Progress value={progress} className="h-2 rounded-none" />

      <div className="flex h-[calc(100vh-80px)]">
        {/* Left panel - Attachments Gallery */}
        <div className="w-1/3 p-4 border-r border-white/10 flex flex-col">
          {/* Main viewer */}
          <div className="flex-1 relative rounded-xl overflow-hidden bg-black/30 mb-4">
            {(() => {
              // Combine main_image_url with attachments
              const allFiles: { url: string; type: 'image' | 'pdf' | 'document'; name: string }[] = [];

              if (activePlybook?.main_image_url) {
                allFiles.push({ url: activePlybook.main_image_url, type: 'image', name: 'Imagen Principal' });
              }

              attachments?.forEach(att => {
                const isImage = att.attachment_type === 'image' || att.file_type?.startsWith('image/');
                const isPdf = att.attachment_type === 'pdf' || att.file_type === 'application/pdf';
                allFiles.push({
                  url: att.file_url,
                  type: isImage ? 'image' : isPdf ? 'pdf' : 'document',
                  name: att.file_name,
                });
              });

              if (allFiles.length === 0) {
                return (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <Paperclip className="w-16 h-16 text-white/20 mx-auto mb-2" />
                      <p className="text-white/40">Sin archivos adjuntos</p>
                    </div>
                  </div>
                );
              }

              const currentFile = allFiles[currentAttachmentIndex] || allFiles[0];
              const hasMultiple = allFiles.length > 1;

              return (
                <>
                  {/* Navigation arrows */}
                  {hasMultiple && (
                    <>
                      <button
                        onClick={() => setCurrentAttachmentIndex(i => i > 0 ? i - 1 : allFiles.length - 1)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button
                        onClick={() => setCurrentAttachmentIndex(i => i < allFiles.length - 1 ? i + 1 : 0)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
                      >
                        <ChevronRightIcon className="w-6 h-6" />
                      </button>
                    </>
                  )}

                  {/* File counter */}
                  {hasMultiple && (
                    <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 px-3 py-1 rounded-full bg-black/50 text-white text-sm">
                      {currentAttachmentIndex + 1} / {allFiles.length}
                    </div>
                  )}

                  {/* Content based on type */}
                  {currentFile.type === 'image' ? (
                    <div
                      className="h-full cursor-pointer group"
                      onClick={() => setImageZoom(true)}
                    >
                      <img
                        src={currentFile.url}
                        alt={currentFile.name}
                        className="w-full h-full object-contain"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <ZoomIn className="w-12 h-12" />
                      </div>
                    </div>
                  ) : currentFile.type === 'pdf' ? (
                    <div className="h-full flex flex-col">
                      <iframe
                        src={currentFile.url}
                        className="flex-1 w-full rounded-lg"
                        title={currentFile.name}
                      />
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center">
                        <File className="w-16 h-16 text-white/40 mx-auto mb-4" />
                        <p className="text-white font-medium mb-2">{currentFile.name}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(currentFile.url, '_blank')}
                          className="gap-2 border-white/30 text-white hover:bg-white/10"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Abrir archivo
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* File name */}
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                    <p className="text-white text-sm truncate text-center">{currentFile.name}</p>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Thumbnails */}
          {(() => {
            const allFiles: { url: string; type: 'image' | 'pdf' | 'document'; name: string }[] = [];
            if (activePlybook?.main_image_url) {
              allFiles.push({ url: activePlybook.main_image_url, type: 'image', name: 'Imagen Principal' });
            }
            attachments?.forEach(att => {
              const isImage = att.attachment_type === 'image' || att.file_type?.startsWith('image/');
              const isPdf = att.attachment_type === 'pdf' || att.file_type === 'application/pdf';
              allFiles.push({
                url: att.file_url,
                type: isImage ? 'image' : isPdf ? 'pdf' : 'document',
                name: att.file_name,
              });
            });

            if (allFiles.length <= 1) return null;

            return (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {allFiles.map((file, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentAttachmentIndex(idx)}
                    className={cn(
                      "flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all",
                      currentAttachmentIndex === idx
                        ? "border-yellow-400 ring-2 ring-yellow-400/50"
                        : "border-white/20 hover:border-white/40"
                    )}
                  >
                    {file.type === 'image' ? (
                      <img src={file.url} alt={file.name} className="w-full h-full object-cover" />
                    ) : file.type === 'pdf' ? (
                      <div className="w-full h-full bg-red-900/50 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-red-400" />
                      </div>
                    ) : (
                      <div className="w-full h-full bg-blue-900/50 flex items-center justify-center">
                        <File className="w-6 h-6 text-blue-400" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            );
          })()}

          {/* Actions */}
          {(() => {
            const allFiles: { url: string; type: 'image' | 'pdf' | 'document'; name: string }[] = [];
            if (activePlybook?.main_image_url) {
              allFiles.push({ url: activePlybook.main_image_url, type: 'image', name: 'Imagen Principal' });
            }
            attachments?.forEach(att => {
              const isImage = att.attachment_type === 'image' || att.file_type?.startsWith('image/');
              const isPdf = att.attachment_type === 'pdf' || att.file_type === 'application/pdf';
              allFiles.push({
                url: att.file_url,
                type: isImage ? 'image' : isPdf ? 'pdf' : 'document',
                name: att.file_name,
              });
            });

            if (allFiles.length === 0) return null;
            const currentFile = allFiles[currentAttachmentIndex] || allFiles[0];

            return (
              <div className="flex gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(currentFile.url, '_blank')}
                  className="flex-1 gap-2 border-white/30 text-white hover:bg-white/10"
                >
                  <ExternalLink className="w-4 h-4" />
                  Abrir
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = currentFile.url;
                    link.download = currentFile.name;
                    link.click();
                  }}
                  className="flex-1 gap-2 border-white/30 text-white hover:bg-white/10"
                >
                  <Download className="w-4 h-4" />
                  Descargar
                </Button>
              </div>
            );
          })()}
        </div>

        {/* Right panel - Zones checklist */}
        <div className="flex-1 p-4">
          <ScrollArea className="h-full">
            <div className="space-y-4">
              {Object.entries(pliesByZone).map(([zone, zonePlies]) => {
                const zoneProgress = getZoneProgress(zonePlies);
                const isZoneComplete = zoneProgress.confirmed === zoneProgress.total;
                const zoneColor = getZoneColor(zone);

                return (
                  <Card
                    key={zone}
                    className={cn(
                      "bg-white/5 border-2 transition-all",
                      isZoneComplete ? "border-green-500/50 bg-green-900/20" : "border-white/10",
                      activeZone === zone && "ring-2 ring-yellow-400"
                    )}
                  >
                    <CardContent className="p-4">
                      {/* Zone header */}
                      <div className="flex items-center gap-4 mb-4">
                        <div
                          className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg"
                          style={{ backgroundColor: zoneColor }}
                        >
                          {isZoneComplete ? <CheckCircle2 className="w-8 h-8" /> : zone.replace('Zona ', '')}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-white">{zone}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Progress value={zoneProgress.percent} className="h-2 flex-1" />
                            <span className="text-sm text-white/60">
                              {zoneProgress.confirmed}/{zoneProgress.total}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Plies in zone */}
                      <div className="grid gap-2">
                        {zonePlies.map((ply) => {
                          const isConfirmed = confirmedPlyNumbers.has(ply.ply_number);
                          const isNext = ply.id === nextPly?.id;
                          const plyColor = ply.color || PLY_COLORS[ply.material_type || 'other'];

                          return (
                            <div
                              key={ply.id}
                              className={cn(
                                "flex items-center gap-3 p-3 rounded-xl transition-all",
                                isConfirmed && "bg-green-600/20",
                                isNext && "bg-yellow-500/20 ring-2 ring-yellow-400 animate-pulse",
                                !isConfirmed && !isNext && "bg-white/5"
                              )}
                            >
                              {/* Ply number badge */}
                              <div
                                className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow"
                                style={{ backgroundColor: plyColor }}
                              >
                                {ply.ply_number}
                              </div>

                              {/* Ply info */}
                              <div className="flex-1">
                                <p className="font-medium text-white">
                                  {ply.material_name || MATERIAL_TYPE_LABELS[ply.material_type || 'other']}
                                </p>
                                <div className="flex items-center gap-2 text-sm text-white/60">
                                  <Badge variant="outline" className="text-xs border-white/30">
                                    {formatFiberAngle(ply.fiber_angle)}
                                  </Badge>
                                  {ply.width_mm && ply.length_mm && (
                                    <span>{ply.width_mm} x {ply.length_mm} mm</span>
                                  )}
                                </div>
                                {ply.special_instructions && (
                                  <p className="text-amber-400 text-sm mt-1 flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    {ply.special_instructions}
                                  </p>
                                )}
                              </div>

                              {/* Status / Action */}
                              {isConfirmed ? (
                                <CheckCircle2 className="w-8 h-8 text-green-500" />
                              ) : isNext ? (
                                <Button
                                  size="lg"
                                  onClick={() => {
                                    handleStartPly(ply);
                                    setConfirmDialog({ open: true, ply });
                                  }}
                                  className="bg-green-600 hover:bg-green-700 gap-2"
                                >
                                  <CheckCircle2 className="w-5 h-5" />
                                  CONFIRMAR
                                </Button>
                              ) : (
                                <Circle className="w-8 h-8 text-white/20" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ open })}>
        <DialogContent className="bg-slate-800 text-white border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-2xl">Confirmar Capa #{confirmDialog.ply?.ply_number}</DialogTitle>
            <DialogDescription className="text-white/60">
              {confirmDialog.ply?.material_name} - {confirmDialog.ply?.fiber_angle !== undefined ? formatFiberAngle(confirmDialog.ply.fiber_angle) : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Timer display */}
            <div className="text-center p-4 bg-black/30 rounded-xl">
              <p className="text-sm text-white/60 mb-1">Tiempo de colocación</p>
              <p className="text-4xl font-mono text-yellow-400">{formatTime(elapsedSeconds)}</p>
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm text-white/60 mb-2 block">Notas (opcional)</label>
              <Textarea
                value={confirmNotes}
                onChange={(e) => setConfirmNotes(e.target.value)}
                placeholder="Observaciones..."
                className="bg-black/30 border-white/20 text-white"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmDialog({ open: false })}
              className="text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmPly}
              disabled={confirmMutation.isPending}
              className="bg-green-600 hover:bg-green-700 gap-2"
            >
              <CheckCircle2 className="w-5 h-5" />
              CONFIRMAR CAPA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image zoom dialog */}
      <Dialog open={imageZoom} onOpenChange={setImageZoom}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black border-0">
          {(() => {
            const allFiles: { url: string; type: 'image' | 'pdf' | 'document'; name: string }[] = [];
            if (activePlybook?.main_image_url) {
              allFiles.push({ url: activePlybook.main_image_url, type: 'image', name: 'Imagen Principal' });
            }
            attachments?.forEach(att => {
              const isImage = att.attachment_type === 'image' || att.file_type?.startsWith('image/');
              const isPdf = att.attachment_type === 'pdf' || att.file_type === 'application/pdf';
              allFiles.push({
                url: att.file_url,
                type: isImage ? 'image' : isPdf ? 'pdf' : 'document',
                name: att.file_name,
              });
            });
            const currentFile = allFiles[currentAttachmentIndex] || allFiles[0];

            if (currentFile?.type === 'image') {
              return (
                <img
                  src={currentFile.url}
                  alt={currentFile.name}
                  className="w-full h-full object-contain"
                />
              );
            } else if (currentFile?.type === 'pdf') {
              return (
                <iframe
                  src={currentFile.url}
                  className="w-full h-full"
                  title={currentFile.name}
                />
              );
            }
            return null;
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
